/**
 *
 *                        ..
 *                  ___,  /'
 *             _.-'` __|_/
 *           .'  ,-:`  |/ `'-,
 *          /  .'    .`|`'.   '.
 *         /  /     '  |  `.    \
 *        |  |     |   |   |     |
 *        |  |     |   |   |     |
 *        |  |     |   |   |     |
 *         \  \    `.  |  /     /
 *          \  `.    `'|.'   .'
 *           '._ `'-._/|__.-'`
 *              `-.__/_|
 *                  /  |
 *                _____|_____
 *               /___________\
 *             Greenwich Loyalty
 *        https://greenwichloyalty.com
 *
**/


// Our libraries
import * as logger from './logger';

// Third party libraries
import dayjs from 'dayjs';

const PROG = "apiComms.js"

const ISO8601_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

const _fetchWithRetry = async (apiUrl, method, body) => {
  // For debugging
  const FUNCTION = '_fetchWithRetry';

  let retryCount = 0;
  let retryDelay = 1000; // Milliseconds
  let haveResult = false;

  const ssoAccessToken = localStorage.getItem('token');

  const MAX_RETRIES = global.apiCallMaxRetries;

  logger.debug('Starting', { PROG, FUNCTION, apiUrl, MAX_RETRIES });

  let returnData = null;

  while (!haveResult && retryCount < MAX_RETRIES) {

    logger.debug('Try/Retry #', { PROG, FUNCTION, apiUrl, retryCount, MAX_RETRIES });

    try {

      let fetchOptions = {
        method : method,
        headers : {
          jwt : ssoAccessToken, // May, or may not, be present or required depending on individual API endpoint
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      logger.debug('fetchOptions', { PROG, FUNCTION, fetchOptions });

      if (body != null) {
        fetchOptions.body = JSON.stringify(body);
      }

      const res = await fetch(apiUrl, fetchOptions);

      const resData = await res.json();
      const resStatus = res.status;

      logger.debug('Response for API call', { PROG, FUNCTION, apiUrl, resStatus, resData });

      returnData = resData;

      if (resStatus == 200) {
        // Good
        logger.debug('Successfully retrieved data from API call', { PROG, FUNCTION, resStatus, resData, retryCount });
        haveResult = true;
      }
      else {
        logger.warn('API did NOT return 200 status', { PROG, FUNCTION, apiUrl, resStatus, resData });
        retryCount++;
        if (retryCount !== global.apiCallMaxRetries) {
          await _delay(retryDelay);
          retryDelay *= 2; // For next time if there is a next time
        }
      }
    }
    catch (e) {
      logger.error('Exception Occured', { PROG, FUNCTION, apiUrl, retryCount });
      console.log('Exception: '+e+', '+e.stack);
      retryCount++;
      if (retryCount !== global.apiCallMaxRetries) {
        await _delay(retryDelay);
        retryDelay *= 2; // For next time if there is a next time
      }
    }
  }
  if (!haveResult) {
    logger.warn('Failed to retrieve a result even though we retried', { PROG, FUNCTION, apiUrl });
    return returnData;
  }
  return returnData;
};

const _delay = async (delayMillis) => {
  // For debugging
  const FUNCTION = '_delay';
  return new Promise(async (resolve, reject) => {
    setTimeout(() => {
      resolve();
    }, delayMillis);
  });
};

export async function sendSignUpInPinCodeToEmail(emailAddress) {
  // For debugging
  const FUNCTION = 'sendSignUpInPinCodeForEmail';

  const apiEndpoint = global.apiBaseUrl + '/signin/pin/'+emailAddress;

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function validatePin(emailAddress, pin) {
  // For debugging
  const FUNCTION = 'validatePin';
  
  const apiEndpoint = global.apiBaseUrl + '/signin/pin/validate/'+emailAddress+'/'+pin;

  const res = await _fetchWithRetry(apiEndpoint, 'POST', {});
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataUsersPerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataUsersPerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/usersPerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataLoyaltySignupsPerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataLoyaltySignupsPerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/loyaltySignupsPerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataCustomersByCounty() {
  // For debugging
  const FUNCTION = 'getChartDataCustomersByCounty';

  const apiEndpoint = global.apiBaseUrl + '/chartdata/customersByCounty';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTotalSpendByCounty(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataTotalSpendByCounty';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/totalSpendByCounty';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataAppSessionsPerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataAppSessionsPerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/appSessionsPerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTransactionsPerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataTransactionsPerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/transactionsPerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTransactionValuePerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataTransactionValuePerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/transactionValuePerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataAverageTransactionValuePerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataAverageTransactionValuePerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/averageTransactionValuePerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataRepeatPurchaseRate(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataRepeatPurchaseRate';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/repeatPurchaseRate';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataRepeatPurchaseRatePerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataRepeatPurchaseRatePerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/repeatPurchaseRatePerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTransactionsPerDayByBranch(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataTransactionsPerDayByBranch';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/transactionsPerDayByBranch';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTransactionValuePerDayByBranch(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataTransactionValuePerDayByBranch';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/transactionValuePerDayByBranch';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataCollectorCardCompletionRate(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataCollectorCardCompletionRate';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/collectorCardCompletionRate';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataCollectorCardsCompletedPerDay(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataCollectorCardsCompletedPerDay';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/collectorCardsCompletedPerDay';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataCollectorCardsInProgressByStampNumber(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataCollectorCardsInProgressByStampNumber';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/collectorCardsInProgressByStampNumber';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataAverageStampsPerActiveCollectorCard(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataAverageStampsPerActiveCollectorCard';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/averageStampsPerActiveCollectorCard';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTopBranchesByStampsIssued(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataTopBranchesByStampsIssued';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/topBranchesByStampsIssued';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataTopBranchesByCollectorCardsEver() {
  // For debugging
  const FUNCTION = 'getChartDataTopBranchesByCollectorCardsEver';

  const apiEndpoint = global.apiBaseUrl + '/chartdata/topBranchesByCollectorCardsEver';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataCollectorCardCompletionFunnel(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataCollectorCardCompletionFunnel';

  let apiEndpoint = global.apiBaseUrl + '/chartdata/collectorCardCompletionFunnel';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getChartDataStampsPerDayByCollectorCard(timeRange) {
  // For debugging
  const FUNCTION = 'getChartDataStampsPerDayByCollectorCard';

  logger.debug('getChartDataStampsPerDayByCollectorCard called', { PROG, FUNCTION });

  let apiEndpoint = global.apiBaseUrl + '/chartdata/stampsPerDayByCollectorCard';
  if (timeRange != null) {
    apiEndpoint += '?timeRange='+encodeURIComponent(timeRange);
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getAllPromotions() {
  // For debugging
  const FUNCTION = 'getAllPromotions';

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/promotions';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function uploadImage(base64ImageData) {
  // For debugging
  const FUNCTION = 'uploadImage';

  const apiEndpoint = global.apiBaseUrl + '/media/image';

  const putBody = {
    base64Image : base64ImageData
  };

  console.log('PUT BODY: '+JSON.stringify(putBody));

  const res = await _fetchWithRetry(apiEndpoint, 'PUT', putBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getImage(imageUrl) {
  // For debugging
  const FUNCTION = 'getImage';

  const apiEndpoint = global.apiBaseUrl + '/media/image/'+encodeURIComponent(imageUrl);

  console.log('apiEndpoint: '+JSON.stringify(apiEndpoint));

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};


export async function addNewPromotion(title, linkUrl, imageUrl, displayFrom, displayTo) {
  // For debugging
  const FUNCTION = 'addNewPromption';

  logger.debug('addNewPromotion called', { PROG, FUNCTION, title, linkUrl, imageUrl, displayFrom, displayTo });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/promotions';

  const putBody = {
    title : title,
    linkUrl : linkUrl,  
    imageUrl : imageUrl,
    displayFrom : displayFrom,
    displayTo : displayTo
  };

  console.log('PUT BODY: '+JSON.stringify(putBody));

  const res = await _fetchWithRetry(apiEndpoint, 'PUT', putBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function updateExistingPromotion(promotionId, title, linkUrl, imageUrl, displayFrom, displayTo) {
  // For debugging
  const FUNCTION = 'updateExistingPromption';

  logger.debug('updateExistingPromotion called', { PROG, FUNCTION, title, linkUrl, imageUrl, displayFrom, displayTo });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/promotions/'+promotionId;

  const postBody = {
    title : title,
    linkUrl : linkUrl,  
    imageUrl : imageUrl,
    displayFrom : displayFrom,
    displayTo : displayTo
  };

  console.log('POST BODY: '+JSON.stringify(postBody));

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};




export async function deletePromotion(promotionId) {
  // For debugging
  const FUNCTION = 'deletePromotion';

  logger.debug('deletePromotion called', { PROG, FUNCTION, promotionId });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/promotions/'+promotionId;

  const res = await _fetchWithRetry(apiEndpoint, 'DELETE', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function updatePromotionsOrders(promotionUpdateRecords) {
  // For debugging
  const FUNCTION = 'updatePromotionsOrders';

  logger.debug('updatePromotionsOrders called', { PROG, FUNCTION, promotionUpdateRecords });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/promotions/order';

  const postBody = {
    order : promotionUpdateRecords
  }

  logger.debug('Post body', { PROG, FUNCTION, apiEndpoint, postBody });

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getAllNonValidatedCoupons() {
  // For debugging
  const FUNCTION = 'getAllNonValidatedCoupons';

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/nonvalidated-coupons';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function deleteNonValidatedCoupon(couponId) {
  // For debugging
  const FUNCTION = 'deleteNonValidatedCoupon';

  logger.debug('Called', { PROG, FUNCTION, couponId });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/nonvalidated-coupons/'+couponId;

  const res = await _fetchWithRetry(apiEndpoint, 'DELETE', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function addNewNonValidatedCoupon(title, linkUrl, imageUrl, displayFrom, displayTo) {
  // For debugging
  const FUNCTION = 'addNewNonValidatedCoupon';

  logger.debug('Called', { PROG, FUNCTION, title, linkUrl, imageUrl, displayFrom, displayTo });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/nonvalidated-coupons';

  const putBody = {
    title : title,
    linkUrl : linkUrl,  
    imageUrl : imageUrl,
    displayFrom : displayFrom,
    displayTo : displayTo
  };

  console.log('PUT BODY: '+JSON.stringify(putBody));

  const res = await _fetchWithRetry(apiEndpoint, 'PUT', putBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function updateExistingNonValidatedCoupon(couponId, title, linkUrl, imageUrl, displayFrom, displayTo) {
  // For debugging
  const FUNCTION = 'updateExistingNonValidatedCoupon';

  logger.debug('Called', { PROG, FUNCTION, couponId, title, linkUrl, imageUrl, displayFrom, displayTo });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/nonvalidated-coupons/'+couponId;

  const postBody = {
    title : title,
    linkUrl : linkUrl,  
    imageUrl : imageUrl,
    displayFrom : displayFrom,
    displayTo : displayTo
  };

  console.log('POST BODY: '+JSON.stringify(postBody));

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function updateNonValidatedCouponOrders(couponUpdateRecords) {
  // For debugging
  const FUNCTION = 'updateNonValidatedCouponOrders';

  logger.debug('updateNonValidatedCouponOrders called', { PROG, FUNCTION, couponUpdateRecords });

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/nonvalidated-coupons/order';

  const postBody = {
    order : couponUpdateRecords
  }

  logger.debug('Post body', { PROG, FUNCTION, apiEndpoint, postBody });

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};


export async function getAllCollectorCards() {
  // For debugging
  const FUNCTION = 'getAllCollectorCards';

  const apiEndpoint = global.apiBaseUrl + '/offers-and-promotions/collector-cards';
  
  return {
    isErr : false,
    errMsg : null,
    response : {
      collectorCards : []
    }
  };

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};
  
export async function getAllBusinessLocations() {
  // For debugging
  const FUNCTION = 'getAllBusinessLocations';

  const apiEndpoint = global.apiBaseUrl + '/business/locations';
  
  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getAllWebstoreLinks() {
  // For debugging
  const FUNCTION = 'getAllWebstoreLinks';

  const apiEndpoint = global.apiBaseUrl + '/webstore/webstore-links';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function updateExistingWebstoreLink(webstoreLinkId, title, displayText, linkUrl, imageUrl, displayFrom, displayTo, linkType) {
  // For debugging
  const FUNCTION = 'updateExistingWebstoreLink';

  logger.debug('updateExistingWebstoreLink called', { PROG, FUNCTION, title, displayText, linkUrl, imageUrl, displayFrom, displayTo, linkType });

  const apiEndpoint = global.apiBaseUrl + '/webstore/webstore-link/'+webstoreLinkId;

  const postBody = {
    title : title,
    displayText : displayText,
    linkUrl : linkUrl,  
    imageUrl : imageUrl,
    displayFrom : displayFrom,
    displayTo : displayTo,
    linkType : linkType
  };

  console.log('POST BODY: '+JSON.stringify(postBody));

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function addNewWebstoreLink(title, displayText, linkUrl, imageUrl, displayFrom, displayTo, linkType) {
  // For debugging
  const FUNCTION = 'addNewWebstoreLink';

  logger.debug('addNewWebstoreLink called', { PROG, FUNCTION, title, displayText, linkUrl, imageUrl, displayFrom, displayTo });

  const apiEndpoint = global.apiBaseUrl + '/webstore/webstore-link';

  const putBody = {
    title : title,
    displayText : displayText,
    linkUrl : linkUrl,  
    imageUrl : imageUrl,
    displayFrom : displayFrom,
    displayTo : displayTo,
    linkType : linkType
  };

  console.log('PUT BODY: '+JSON.stringify(putBody));

  const res = await _fetchWithRetry(apiEndpoint, 'PUT', putBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function deleteWebstoreLink(webstoreLinkId) {
  // For debugging
  const FUNCTION = 'deleteWebstoreLink';

  logger.debug('deleteWebstoreLink called', { PROG, FUNCTION, webstoreLinkId });

  const apiEndpoint = global.apiBaseUrl + '/webstore/webstore-link/'+webstoreLinkId;

  const res = await _fetchWithRetry(apiEndpoint, 'DELETE', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function updateWebstoreLinkOrders(orderUpdateRecords) {
  // For debugging
  const FUNCTION = 'updateWebstoreLinkOrders';

  logger.debug('updateWebstoreLinkOrders called', { PROG, FUNCTION, orderUpdateRecords });

  const apiEndpoint = global.apiBaseUrl + '/webstore/webstore-link/order';

  const postBody = {
    order : orderUpdateRecords
  }

  logger.debug('Post body', { PROG, FUNCTION, apiEndpoint, postBody });

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};


/**
export async function getAddUsers() {
  // For debugging
  const FUNCTION = 'getAllUsers';

  logger.debug('getAllUsers called', { PROG, FUNCTION });

  const apiEndpoint = global.apiBaseUrl + '/users/users';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};
**/

export async function getUsers(orderBy, nextPageToken, searchText) {
  // For debugging
  const FUNCTION = 'getLoyaltyUsers';

  logger.debug('getLoyaltyUsers called', { PROG, FUNCTION });

  let apiEndpoint = global.apiBaseUrl + '/users/users?orderBy='+orderBy;
  if (searchText != null && searchText.trim() !== '') {
    apiEndpoint += '&searchText='+encodeURIComponent(searchText.trim());
  }
  if (nextPageToken != null) {
    apiEndpoint += '&nextPageToken='+encodeURIComponent(JSON.stringify(nextPageToken));
  }

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};
  
export async function getUserStats(accountId) { // Meridian AccountKey
  // For debugging
  const FUNCTION = 'getUserStats';

  logger.debug('getUserStats called', { PROG, FUNCTION });

  let apiEndpoint = global.apiBaseUrl + '/users/user-stats/'+accountId;

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getUser(accountId) {
  // For debugging
  const FUNCTION = 'getUser';

  logger.debug('getUser called', { PROG, FUNCTION, accountId });

  let apiEndpoint = global.apiBaseUrl + '/users/user/'+encodeURIComponent(accountId);

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getUserCoupons(accountId) {
  const apiEndpoint = global.apiBaseUrl + '/users/user-coupons/'+encodeURIComponent(accountId);
  return _fetchWithRetry(apiEndpoint, 'GET', null);
};

export async function getCouponsOverview() {
  const apiEndpoint = global.apiBaseUrl + '/coupons/overview';
  return _fetchWithRetry(apiEndpoint, 'GET', null);
};

export async function getCouponRedemptionDetail(redemptionId) {
  const apiEndpoint = global.apiBaseUrl + '/coupons/redemption/'+encodeURIComponent(redemptionId);
  return _fetchWithRetry(apiEndpoint, 'GET', null);
};

export async function getCouponCollectionHistory(redemptionId) {
  const apiEndpoint = global.apiBaseUrl + '/coupons/redemption/'+encodeURIComponent(redemptionId)+'/history';
  return _fetchWithRetry(apiEndpoint, 'GET', null);
};

export async function getAllUserCount() {
  // For debugging
  const FUNCTION = 'getAllUserCount';

  logger.debug('getAllUserCount called', { PROG, FUNCTION });

  let apiEndpoint = global.apiBaseUrl + '/users/count';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function getAllFiles() {
  // For debugging
  const FUNCTION = 'getAllFiles';

  logger.debug('getUserStats called', { PROG, FUNCTION });

  let apiEndpoint = global.apiBaseUrl + '/files';

  const res = await _fetchWithRetry(apiEndpoint, 'GET', null);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

export async function uploadFile(filename, content) {
  // For debugging
  const FUNCTION = 'uploadFile';

  logger.debug('uploadFile called', { PROG, FUNCTION, filename });

  const apiEndpoint = global.apiBaseUrl + '/files/file/'+filename;

  const postBody = {
    base64Content : content
  }

  logger.debug('Post body', { PROG, FUNCTION, apiEndpoint });

  const res = await _fetchWithRetry(apiEndpoint, 'POST', postBody);
  logger.debug('Retrieved from api', { PROG, FUNCTION, apiEndpoint, res });
  return res;
};

  

  
  
  
  

  
  
  

  

 
  
  



  
  
